import React from 'react';
import { useRouter } from 'next/router';

import { isValidNavigationStep } from 'helpers/UrlHelpers';

interface Options {
  startOnInitialize?: boolean,
  ignoreMobilePreview?: boolean,
  ignoreShallowWhileCounting?: boolean,
  ignoredUrlsWhileCounting?: string[],
  invalidUrls?: string[],
  navigationStepValidator?: (url: string) => boolean,
  onNavigationStart?: () => void,
  onNavigationError?: () => void,
  onNavigationComplete?: (url: string) => void
}

export function useNavigationStepCounter(options: Options = {}) {
  const {
    startOnInitialize = true,
    ignoredUrlsWhileCounting,
    ignoreShallowWhileCounting = true,
    ignoreMobilePreview,
    invalidUrls,
    navigationStepValidator,
    onNavigationStart,
    onNavigationError,
    onNavigationComplete
  } = options;

  const [count, setCount] = React.useState(0);
  const [isRunning, setRunning] = React.useState(startOnInitialize);

  const router = useRouter();
  const previousUrl = React.useRef<string | undefined>(router.pathname);

  /* Actions */

  const start = React.useCallback(() => {
    setRunning(true);
  }, []);

  const stop = React.useCallback(() => {
    setRunning(false);
  }, []);

  const reset = React.useCallback(() => {
    setCount(0);
  }, []);

  /* Handlers */

  const handleNavigationStart = React.useCallback(() => {
    if (onNavigationStart) {
      onNavigationStart();
    }
  }, [onNavigationStart]);

  const handleNavigationError = React.useCallback(() => {
    if (onNavigationError) {
      onNavigationError();
    }
  }, [onNavigationError]);

  const handleNavigationComplete = React.useCallback((url: string, params?: {shallow?: boolean}) => {

    const { shallow } = params || {};

    if (shallow && ignoreShallowWhileCounting) {
    } else {
      const isValidUrl = navigationStepValidator
        ? navigationStepValidator(url)
        : isValidNavigationStep(url, { ignoredUrls: ignoredUrlsWhileCounting, ignoreMobilePreview });

      if (isValidUrl) {
        setCount((prev) => prev + 1);
      }
    }

    previousUrl.current = url;
    if (onNavigationComplete) {
      onNavigationComplete(url);
    }
  }, [
    onNavigationComplete,
    navigationStepValidator,
    ignoreMobilePreview,
    ignoredUrlsWhileCounting,
    ignoreShallowWhileCounting
  ]);

  /* Effects */

  React.useEffect(() => {
    if (isRunning) {
      router.events.on('routeChangeStart', handleNavigationStart);
      router.events.on('routeChangeError', handleNavigationError);
      router.events.on('routeChangeComplete', handleNavigationComplete);
    } else {
      router.events.off('routeChangeStart', handleNavigationStart);
      router.events.off('routeChangeError', handleNavigationError);
      router.events.off('routeChangeComplete', handleNavigationComplete);
    }

    return () => {
      router.events.off('routeChangeStart', handleNavigationStart);
      router.events.off('routeChangeError', handleNavigationError);
      router.events.off('routeChangeComplete', handleNavigationComplete);
    };
  }, [
    isRunning,
    handleNavigationComplete,
    handleNavigationError,
    handleNavigationStart,
    router.events
  ]);

  return {
    isRunning,
    isValidStep: invalidUrls
      ? isValidNavigationStep(router.pathname, { ignoredUrls: invalidUrls })
      : true,
    url: router.pathname,
    previousUrl: previousUrl.current,
    start,
    reset,
    stop,
    steps: count
  };
}
